import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { type KeyboardEvent, useCallback, useEffect, useState } from "react";

export interface UseCarouselOptions {
  /** Snap alignment for slides within the viewport. Default: `"center"`. */
  align?: "start" | "center";
  /** Total number of slides, used to window the pagination indicators. */
  slideCount: number;
}

export interface CarouselIndicator {
  index: number;
  size: "full" | "small";
}

export interface UseCarouselReturn {
  /** Attach to the scroll viewport element to initialise embla. */
  emblaRef: UseEmblaCarouselType[0];
  /** True when a previous snap exists (show the prev control). */
  canScrollPrev: boolean;
  /** True when a next snap exists (show the next control). */
  canScrollNext: boolean;
  /** Index of the currently selected snap. */
  selectedIndex: number;
  /** Snap positions; `scrollSnaps.length` is the page count for dots and announcements. */
  scrollSnaps: number[];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  /** ArrowLeft/ArrowRight navigation; attach via `onKeyDownCapture` on the carousel region. */
  handleKeyDown: (event: KeyboardEvent) => void;
  /** Windowed pagination indicators (max 5 visible; edge dots shrink) for the current position. */
  visibleIndicators: CarouselIndicator[];
}

function getVisibleIndicators(currentIndex: number, totalSlides: number): CarouselIndicator[] {
  if (totalSlides <= 5) {
    return Array.from({ length: totalSlides }, (_, i) => ({
      index: i,
      size: "full" as const,
    }));
  }

  // First 3 slides: [full, full, full, full, small]
  if (currentIndex <= 2) {
    return [
      { index: 0, size: "full" },
      { index: 1, size: "full" },
      { index: 2, size: "full" },
      { index: 3, size: "full" },
      { index: 4, size: "small" },
    ];
  }

  // Last 3 slides: [small, full, full, full, full]
  if (currentIndex >= totalSlides - 3) {
    return [
      { index: totalSlides - 5, size: "small" },
      { index: totalSlides - 4, size: "full" },
      { index: totalSlides - 3, size: "full" },
      { index: totalSlides - 2, size: "full" },
      { index: totalSlides - 1, size: "full" },
    ];
  }

  // Middle: [small, full, full, full, small]
  return [
    { index: currentIndex - 2, size: "small" },
    { index: currentIndex - 1, size: "full" },
    { index: currentIndex, size: "full" },
    { index: currentIndex + 1, size: "full" },
    { index: currentIndex + 2, size: "small" },
  ];
}

/** Embla initialisation, scroll-state tracking, arrow-key navigation and pagination-dot
 *  windowing for `<Carousel>`. Owns the `embla-carousel-react` dependency so styled
 *  consumers never touch embla directly. */
export function useCarousel({ align = "center", slideCount }: UseCarouselOptions): UseCarouselReturn {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align,
    containScroll: "trimSnaps",
    duration: 20,
    skipSnaps: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onInit = () => setScrollSnaps(emblaApi.scrollSnapList());
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    onInit();
    onSelect();

    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("reInit", onInit);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  const visibleIndicators = getVisibleIndicators(selectedIndex, slideCount);

  return {
    emblaRef,
    canScrollPrev,
    canScrollNext,
    selectedIndex,
    scrollSnaps,
    scrollPrev,
    scrollNext,
    scrollTo,
    handleKeyDown,
    visibleIndicators,
  };
}
